import axios from 'axios';

// Use relative URL for same-domain deployments (like Vercel)
// Use full URL only for cross-domain deployments
const API_URL = process.env.NODE_ENV === 'development' 
  ? '/api' 
  : (process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api');

// Create axios instance with credentials
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

// Add request interceptor for debugging
api.interceptors.request.use(request => {
  return request;
});

// Add response interceptor for debugging
api.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    console.error('Compass API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
      withCredentials: error.config?.withCredentials
    });
    return Promise.reject(error);
  }
);

export const getAccountBriefs = async (limit = 5) => {
  try {
    const response = await api.get(`/compass/account-briefs`, {
      params: { limit }
    });
    
    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Failed to fetch account briefs');
    }
    
    return response.data.data;
  } catch (error) {
    console.error('Error fetching account briefs:', error);
    throw error.response?.data?.message || 'Failed to fetch account briefs';
  }
};

export const saveAccountBrief = async (data) => {
  try {
    const response = await api.post(`/compass/account-briefs`, data);
    
    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Failed to save account brief');
    }
    
    return response.data.data;
  } catch (error) {
    console.error('Error saving account brief:', error);
    throw error.response?.data?.message || 'Failed to save account brief';
  }
};

export const getBattleCards = async (limit = 5) => {
  try {
    const response = await api.get(`/compass/battle-cards`, {
      params: { limit }
    });
    
    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Failed to fetch battle cards');
    }
    
    return response.data.data;
  } catch (error) {
    console.error('Error fetching battle cards:', error);
    throw error.response?.data?.message || 'Failed to fetch battle cards';
  }
};

export const saveBattleCard = async (data) => {
  try {
    const response = await api.post(`/compass/battle-cards`, data);
    
    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Failed to save battle card');
    }
    
    return response.data.data;
  } catch (error) {
    console.error('Error saving battle card:', error);
    throw error.response?.data?.message || 'Failed to save battle card';
  }
};

export const getObjectionHandling = async (limit = 5) => {
  try {
    const response = await api.get(`/compass/objection-handling`, {
      params: { limit }
    });
    
    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Failed to fetch objection handling');
    }
    
    return response.data.data;
  } catch (error) {
    console.error('Error fetching objection handling:', error);
    throw error.response?.data?.message || 'Failed to fetch objection handling';
  }
};

export const saveObjectionHandling = async (data) => {
  try {
    const response = await api.post(`/compass/objection-handling`, data);
    
    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Failed to save objection handling');
    }
    
    return response.data.data;
  } catch (error) {
    console.error('Error saving objection handling:', error);
    throw error.response?.data?.message || 'Failed to save objection handling';
  }
};

export const updateDocument = async (documentType, documentId, data) => {
  try {
    const response = await api.put(`/compass/${documentType}/${documentId}`, data);
    
    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Failed to update document');
    }
    
    return response.data.data;
  } catch (error) {
    console.error('Error updating document:', error);
    throw error.response?.data?.message || 'Failed to update document';
  }
};

export const deleteAccountBrief = async (id) => {
  try {
    const response = await api.delete(`/compass/account-briefs/${id}`);
    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Failed to delete account brief');
    }
    return response.data;
  } catch (error) {
    console.error('Error deleting account brief:', error);
    throw error.response?.data?.message || 'Failed to delete account brief';
  }
};

export const deleteBattleCard = async (id) => {
  try {
    const response = await api.delete(`/compass/battle-cards/${id}`);
    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Failed to delete battle card');
    }
    return response.data;
  } catch (error) {
    console.error('Error deleting battle card:', error);
    throw error.response?.data?.message || 'Failed to delete battle card';
  }
};

export const deleteObjectionHandling = async (id) => {
  try {
    const response = await api.delete(`/compass/objection-handling/${id}`);
    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Failed to delete objection handling');
    }
    return response.data;
  } catch (error) {
    console.error('Error deleting objection handling:', error);
    throw error.response?.data?.message || 'Failed to delete objection handling';
  }
};

export const getAccountBriefById = async (id) => {
  try {
    const response = await api.get(`/compass/account-briefs/${id}`);
    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Failed to fetch account brief');
    }
    return response.data.data;
  } catch (error) {
    console.error('Error fetching account brief:', error);
    throw error.response?.data?.message || 'Failed to fetch account brief';
  }
};

export const getBattleCardById = async (id) => {
  try {
    const response = await api.get(`/compass/battle-cards/${id}`);
    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Failed to fetch battle card');
    }
    return response.data.data;
  } catch (error) {
    console.error('Error fetching battle card:', error);
    throw error.response?.data?.message || 'Failed to fetch battle card';
  }
};

export const getObjectionHandlingById = async (id) => {
  try {
    const response = await api.get(`/compass/objection-handling/${id}`);
    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Failed to fetch objection handling');
    }
    return response.data.data;
  } catch (error) {
    console.error('Error fetching objection handling:', error);
    throw error.response?.data?.message || 'Failed to fetch objection handling';
  }
}; 